import React, { useMemo, useState, useEffect } from 'react';
import styles from './index.module.scss';
// import CReactMarkdown from '../CReactMarkdown';
import CMarkdown from '../CMarkdown';

interface ActionItem {
  id: string;
  type?: string;
  content: string;
  status: string;
  toolExecResult?: string;
  costTime?: number;
  aiThought?: string;
  actionDetail?: string;
}

interface TaskItem {
  id?: string;
  title?: string;
  desc?: string;
  actionList?: ActionItem[];
}

interface ReasoningProcessProps {
  actionList: ActionItem[];
  taskList?: TaskItem[];
  outputStatus?: string; // 输出状态：FINISHED、WIP
  thoughtProgressFinished?: boolean; // 思考进度是否完成
}

/**
 * 推理过程（旧版交互方式【第一版交互方式，用来兼容旧的历史会话的展示】）
 * @param props 
 * @returns 
 */
const ReasoningProcess: React.FC<ReasoningProcessProps> = ({ actionList, taskList = [], thoughtProgressFinished = false }) => {
  // 管理每个步骤的展开状态
  const [expandedSteps, setExpandedSteps] = useState<{ [key: string]: boolean }>({});
  const [showTaskReasoning, setShowTaskReasoning] = useState(true); // 是否展示任务推理（taskList）
  const [showReasoning, setShowReasoning] = useState(true); // 思考推理（actionList）

  useEffect(() => {
    if (thoughtProgressFinished) {
      setShowTaskReasoning(false);
      setShowReasoning(false);
    }
  }, [thoughtProgressFinished]);

  // 每次 actionList 变化时，自动根据 status 重置展开/收起状态
  useEffect(() => {
    const newExpanded: Record<string, boolean> = {};
    actionList.forEach((item, idx) => {
      const status = item.status?.toLowerCase();
      const stepId = item.id || `step-${idx}`;
      if (status === 'processing') {
        newExpanded[stepId] = true;
      } else if (item.type === 'AI_THOUGHT' && status === 'success') {
        newExpanded[stepId] = false;
      }
    });
    setExpandedSteps(newExpanded);
  }, [actionList]);

  // 去重 actionList
  const deduplicatedActionList = useMemo(() => {
    const uniqueItems = new Map<string, ActionItem>();
    actionList?.forEach(item => {
      if (item?.id) {
        uniqueItems.set(item.id, item);
      }
    });
    return Array.from(uniqueItems.values());
  }, [actionList]);

  const currentRunningIndex = useMemo(() => {
    const runningIndex = deduplicatedActionList.findIndex(item => item.status.toLowerCase() === 'processing');
    return runningIndex >= 0 ? runningIndex : deduplicatedActionList.length - 1;
  }, [deduplicatedActionList]);

  // 判断步骤是否应该展开
  const isStepExpanded = (item: ActionItem, index: number) => {
    const status = item.status.toLowerCase();
    const stepId = item.id || `step-${index}`;
    if (status === 'processing') return true;
    if (item.type === 'AI_THOUGHT' && item.aiThought && item.aiThought.trim()) {
      if (status === 'success') return expandedSteps[stepId] || false;
      if (!(stepId in expandedSteps)) return true;
      return expandedSteps[stepId];
    }
    return expandedSteps[stepId] || false;
  };

  // 切换展开状态
  const toggleStepExpansion = (stepId: string, status: string) => {
    if (status.toLowerCase() === 'processing') return;
    setExpandedSteps(prev => ({ ...prev, [stepId]: !prev[stepId] }));
  };

  const formatTime = (milliseconds: number): string => {
    if (!milliseconds && milliseconds !== 0) return '';
    const totalSeconds = milliseconds / 1000;
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = Math.floor(totalSeconds % 60);
    const ms = Math.floor(milliseconds % 1000);
    const secondsWithDecimal = (seconds + (ms / 1000)).toFixed(2);
    if (hours > 0) {
      return `用时：${hours}小时 ${minutes}分钟`;
    } else if (minutes > 0) {
      return `用时：${minutes}分钟`;
    } else if (seconds > 0) {
      return `用时：${secondsWithDecimal}秒`;
    } else {
      return `用时：${ms}毫秒`;
    }
  };

  // 状态图标
  const getStatusIcon = (status: string, type?: string) => {
    const normalizedStatus = status.toLowerCase();
    if (type === 'AI_THOUGHT') {
      if (normalizedStatus === 'processing') {
        return (
          <div className={styles.processingIcon}>
            <div className={styles.spinner}></div>
          </div>
        );
      } else {
        return (
          <div className={styles.thinkingIcon}>
            <svg width="16" height="16" viewBox="0 0 1024 1024" fill="none"><path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.2 0-372-166.8-372-372S306.8 140 512 140s372 166.8 372 372-166.8 372-372 372zm-56-232l-144-144 56-56 88 88 200-200 56 56-256 256z" fill="#00B54B"></path></svg>
          </div>
        );
      }
    }
    switch (normalizedStatus) {
      case 'processing':
        return (
          <div className={styles.processingIcon}>
            <div className={styles.spinner}></div>
          </div>
        );
      case 'success':
        return (
          <div className={styles.successIcon}>
            <svg width="16" height="16" viewBox="0 0 1024 1024" fill="none"><path d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.2 0-372-166.8-372-372S306.8 140 512 140s372 166.8 372 372-166.8 372-372 372zm-56-232l-144-144 56-56 88 88 200-200 56 56-256 256z" fill="#00B54B"></path></svg>
          </div>
        );
      case 'failed':
        return (
          <div className={styles.failedIcon}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#ff4d4f"/>
            </svg>
          </div>
        );
      default:
        return (
          <div className={styles.pendingIcon}>
            <div className={styles.pendingDot}></div>
          </div>
        );
    }
  };

  // 渲染步骤内容
  const renderSteps = (list: ActionItem[]) => (
    <div className={styles.reasoningProcess}>
      {list.map((item, index) => {
        const status = item.status.toLowerCase() || (index === currentRunningIndex ? 'processing' : 'completed');
        const stepId = item.id || `step-${index}`;
        const isExpanded = isStepExpanded(item, index);
        const hasThought = item.aiThought && item.aiThought.trim(); // AI思考
        const hasParam = item.actionDetail && item.actionDetail.trim(); // 工具调用
        const canToggle = status !== 'processing' && (hasThought || hasParam); // 是否可以展开
        const isAIThought = item.type === 'AI_THOUGHT'; // 是否是AI思考
        return (
          <div key={stepId} className={`${styles.reasoningStep} ${isAIThought ? styles.aiThoughtStep : ''}`}>
            <div 
              className={`${styles.stepHeader} ${canToggle ? styles.clickable : ''}`}
              onClick={() => canToggle && toggleStepExpansion(stepId, status)}
            >
              <div className={styles.stepStatus}>
                {getStatusIcon(status, item.type)}
                <span className={styles.stepTitle}>
                  {item.content}
                </span>
                {canToggle && (
                  <div className={`${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M8 10l4 4 4-4" stroke="#86909C" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                  </div>
                )}
              </div>
            </div>
            {isExpanded && (hasThought || hasParam) && (
              <div className={styles.stepContent}>
                {/* 工具调用 */}
                {hasParam && !isAIThought && (
                  <div className={styles.paramSection}>
                    {/* <div className={styles.paramLabel}>{item.actionDetail}</div> */}
                    {/* <CReactMarkdown content={item.actionDetail} /> */}
                    <CMarkdown content={item.actionDetail} className={styles.customCMarkdown} />
                  </div>
                )}
                {/* AI思考 */}
                {hasThought && (
                  <div className={`${styles.thoughtSection} ${isAIThought ? styles.aiThoughtContent : ''}`}>
                    <div className={styles.thoughtContent}>
                      {item.aiThought?.trim()}
                      {status === 'processing' && isAIThought && (
                        <span className={styles.typingCursor}>|</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // 渲染 taskList
  if (taskList && taskList.length > 0) {
    return (
      <div className={styles.reasoningContainer}>
        <div className={styles.reasoningHeader} onClick={() => setShowTaskReasoning(v => !v)}>
          <span className={styles.reasoningTitle}>
            <img src="https://imgcdn.sto.cn/F6EC6438-3259-44-18x13-%E5%BD%A2%E7%8A%B6.svg" alt="" style={{ width: '18px', height: '13px', display: 'block', marginRight: '4px' }} />
            申懂懂多智能体{thoughtProgressFinished ? '已完成推理' : '推理中...'}
            <svg className={`${styles.arrow} ${showTaskReasoning ? styles.arrowUp : ''}`} viewBox="0 0 1024 1024" width="14" height="14" fill="#86909C">
              <path d="M512 714.667c-8.533 0-17.067-2.134-23.467-8.534L147.2 364.8c-12.8-12.8-12.8-32 0-44.8 12.8-12.8 32-12.8 44.8 0L512 640 832 320c12.8-12.8 32-12.8 44.8 0 12.8 12.8 12.8 32 0 44.8L535.467 706.133c-6.4 6.4-14.934 8.534-23.467 8.534z"></path>
            </svg>
          </span>
        </div>
        {showTaskReasoning && (
          <div style={{ margin: 10 }}>
            {taskList.map((task, idx) => (
              <div key={task.id || idx} style={{ marginBottom: 6, background: '#F7F8FA', borderRadius: 8, padding: '12px 12px 0' }}>
                <div style={{ fontWeight: 500, color: '#7b68ee', marginBottom: 6 }}>步骤 {idx + 1}：{task.title || task.desc || '无标题'}</div>
                {task.actionList && task.actionList.length > 0 && renderSteps(task.actionList)}
                {task.desc && <div style={{ color: '#86909C', fontSize: 12, marginTop: 4 }}>{task.desc}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // 否则就渲染 actionList
  if (deduplicatedActionList && deduplicatedActionList.length > 0) {
    return (
      <div className={styles.reasoningContainer}>
        <div className={styles.reasoningHeader} onClick={() => setShowReasoning(v => !v)}>
          <span className={styles.reasoningTitle}>
            <img src="https://imgcdn.sto.cn/F6EC6438-3259-44-18x13-%E5%BD%A2%E7%8A%B6.svg" alt="" style={{ width: '18px', height: '13px', display: 'block', marginRight: '4px' }} />
            申懂懂多智能体{thoughtProgressFinished ? '已完成推理' : '推理中...'}
            <svg className={`${styles.arrow} ${showReasoning ? styles.arrowUp : ''}`} viewBox="0 0 1024 1024" width="14" height="14" fill="#86909C">
              <path d="M512 714.667c-8.533 0-17.067-2.134-23.467-8.534L147.2 364.8c-12.8-12.8-12.8-32 0-44.8 12.8-12.8 32-12.8 44.8 0L512 640 832 320c12.8-12.8 32-12.8 44.8 0 12.8 12.8 12.8 32 0 44.8L535.467 706.133c-6.4 6.4-14.934 8.534-23.467 8.534z"></path>
            </svg>
          </span>
        </div>
        {showReasoning && renderSteps(deduplicatedActionList)}
      </div>
    );
  }

  return null;
};

export default ReasoningProcess;
